import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { batch } from "react-redux";
import Axios from "axios";
import { API_URL } from "config";
import { RootState } from "store/rootReducer";
import { Order, OrdersInitialState } from "./types";
import { ProductCartItem } from "features/cart/types";
import userStorage from "store/UserStorage";
import { setResponse, setResponseMessage } from "../adminSlice";
import { ObjectType } from "features/types";

const initialState: OrdersInitialState = {
  orders: [],
  isLoading: false,
  hasError: false,
  orderElements: [],
  areElementsLoading: false,
  elementsHasError: false,
  editOrder: undefined,
};

const ordersAdmin = createSlice({
  name: "ordersAdminSlice",
  initialState,
  reducers: {
    setOrders(state, action: PayloadAction<Order[]>) {
      state.orders = action.payload;
      state.isLoading = false;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<boolean>) {
      state.hasError = action.payload;
    },
    setOrderElements(state, action: PayloadAction<ProductCartItem[]>) {
      state.orderElements = action.payload;
      state.areElementsLoading = false;
    },
    setEditOrder(state, action) {
      state.editOrder = action.payload;
      state.isLoading = false;
    },
    setElementsLoading(state, action: PayloadAction<boolean>) {
      state.areElementsLoading = action.payload;
    },
    setElementsHasError(state, action: PayloadAction<boolean>) {
      state.elementsHasError = action.payload;
      state.orderElements = [];
      state.isLoading = false;
    },
  },
});
export const {
  setOrders,
  setLoading,
  setError,
  setOrderElements,
  setEditOrder,
  setElementsLoading,
  setElementsHasError,
} = ordersAdmin.actions;

export const selectAdminOrders = (state: RootState) => state.ordersAdmin.orders;
export const selectAdminOrdersIsLoading = (state: RootState) =>
  state.ordersAdmin.isLoading;
export const selectAdminOrdersHasError = (state: RootState) =>
  state.ordersAdmin.hasError;
export const selectOrderElementsByObjectType = (
  state: RootState,
  objectTypes: string[]
) => {
  return state.ordersAdmin.orderElements.filter((element) =>
    objectTypes.includes(element.product.objectType)
  );
};
export const selectOrderElements = (state: RootState) =>
  state.ordersAdmin.orderElements;
export const selectElementsLoading = (state: RootState) =>
  state.ordersAdmin.areElementsLoading;
export const selectElementsHaveError = (state: RootState) =>
  state.ordersAdmin.elementsHasError;
export const selectEditOrder = (state: RootState) =>
  state.ordersAdmin.editOrder;

export const fetchOrders = () => async (dispatch: any) => {
  dispatch(setLoading(true));
  try {
    const result = await Axios.get(`${API_URL}/checkout?size=9999`);
    dispatch(
      setOrders(
        result.data.data.map((item: any, idx: any) => {
          return { refId: item._id, id: idx + 1, ...item };
        })
      )
    );
  } catch (error: any) {
    batch(() => {
      dispatch(setError(true));
      dispatch(setLoading(false));
    });
  }
};

const ObjectTypeToURL: { [key: string]: string } = {
  [ObjectType.product]: "/shopping-bag-items",
  [ObjectType.accessory]: "/shopping-bag-items-acc",
};

interface OrderedElement {
  _id: string;
  id: string;
  count: number;
  installation: boolean;
  objectType: ObjectType;
}

export const fetchOrderedElements = (items: OrderedElement[]) => async (
  dispatch: any
) => {
  dispatch(setElementsLoading(true));
  try {
    const itemsByObjectType: { [key: string]: OrderedElement[] } = {
      [ObjectType.product]: [],
      [ObjectType.accessory]: [],
    };

    items.forEach((item) => {
      itemsByObjectType[item.objectType].push(item);
    });

    const result = await Promise.all(
      Object.keys(itemsByObjectType).map(async (item) => {
        const query = itemsByObjectType[item].map(
          (innerProduct) => `ids=${innerProduct.id}`
        );

        if (query.length === 0) {
          return;
        }

        const products = await Axios.get(
          `${API_URL}${ObjectTypeToURL[item]}?${query.join("&")}`
        );

        return products.data.data.map((product: any) => {
          return {
            product,
            count: items.find((prod) => prod.id === product.refId)?.count || 0,
            installation:
              items.find((prod) => prod.id === product.refId)?.installation ||
              false,
          };
        });
      })
    );

    dispatch(
      setOrderElements(
        result.filter((item) => item !== undefined).flatMap((item) => item)
      )
    );
  } catch (error: any) {
    console.log(error);
    dispatch(setElementsHasError(true));
  }
};

export const setOrderCompleted = (orderData: Order) => async (
  dispatch: any
) => {
  try {
    let token = userStorage.getUser();
    let completedOrder = { ...orderData };
    completedOrder.order_completed = true;

    await Axios.put(
      `${API_URL}/checkout/${completedOrder.refId}`,
      { data: completedOrder },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    batch(() => {
      dispatch(fetchOrders());
    });
  } catch (error: any) {
    dispatch(setError(true));
  }
};

export const setOrderAgreed = (orderData: Order) => async (dispatch: any) => {
  try {
    let token = userStorage.getUser();
    let agreedOrder = { ...orderData };
    agreedOrder.isAgreed = true;

    await Axios.put(
      `${API_URL}/checkout/${agreedOrder.refId}`,
      { data: agreedOrder },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    batch(() => {
      dispatch(fetchOrders());
    });
  } catch (error: any) {
    dispatch(setError(true));
  }
};

export const deleteOrder = (refId: string) => async (dispatch: any) => {
  dispatch(setLoading(true));
  try {
    let token = userStorage.getUser();
    const response = await Axios.delete(`${API_URL}/checkout/${refId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    batch(() => {
      dispatch(
        setResponse({
          message: "Успешно изтрихте поръчка",
          hasError: false,
        })
      );
      dispatch(setLoading(false));
      dispatch(fetchOrders());
    });
  } catch (error: any) {
    dispatch(setResponse({ message: error.message, hasError: true }));
  }
};

export const fetchOrderForEdit = (id: string) => async (dispatch: any) => {
  dispatch(setLoading(true));
  try {
    const response = await Axios.get(
      `${API_URL}/admin/shopping-bag-values/${id}`
    );
    dispatch(setEditOrder(response.data.data.data));
  } catch (error: any) {
    console.log("error in ordersAdminSlice fetchOrderForEdit");
    dispatch(setError(true));
  }
};

export const updateOrder = (
  data: any,
  id: string,
  onUploadProgress: (e: ProgressEvent<EventTarget>) => void
) => async (dispatch: any) => {
  try {
    let token = userStorage.getUser();
    await Axios.put(
      `${API_URL}/checkout/${id}`,
      { data: data },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        onUploadProgress,
      }
    );

    dispatch(
      setResponseMessage({
        message: "Успешно променихте поръчка.",
        hasError: false,
      })
    );
  } catch (error: any) {
    console.log("error in ordersAdminSlice updateOrder");
    dispatch(setError(true));
  }
};

export default ordersAdmin.reducer;
